/**
 * Element module.
 * @module src/element
 */
import { getCSSProperty, addClass, removeClass } from './style';
/**
 * Returns if an element has or not some boolean property.
 *
 * @function elementIs
 *
 * @param {string} property - the property to be asked
 * @param {HTMLElement} element - the HTML element
 * @return {boolean} - if the element has or not true value to this property
 *
 * @example
 * const element = document.createElement('div');
 * elementIs('disabled', element);
 * // returns false
 *
 * @example
 * const element = document.createElement('div');
 * disableElement(element);
 * elementIs('disabled', element);
 * // returns true
 *
 */

export const elementIs = (property, element) => element[property] === true;
/**
 * Returns whether an element is disabled or not.
 *
 * @function isDisabled
 *
 * @param {HTMLElement} element - the HTML element
 * @return {boolean} - whether the element is or not disabled
 *
 * @example
 * const element = document.createElement('div');
 * isDisabled(element);
 * // returns false
 *
 * @example
 * const element = document.createElement('div');
 * disableElement(element);
 * isDisabled(element);
 * // returns true
 *
 */

export const isDisabled = element => elementIs('disabled', element);
/**
 * Returns if an element is enabled or not.
 *
 * @function isEnabled
 *
 * @param {HTMLElement} element - the HTML element
 * @return {boolean} - if the element is or not enabled
 *
 * @example
 * const element = document.createElement('div');
 * isEnabled(element);
 * // returns true
 *
 * @example
 * const element = document.createElement('div');
 * disableElement(element);
 * isEnabled(element);
 * // returns false
 *
 */

export const isEnabled = element => !isDisabled(element);
/**
 * Returns if an element is hidden or not.
 *
 * @function isHidden
 *
 * @param {HTMLElement} element - the HTML element
 * @return {boolean} - if the element is hidden or not
 *
 * @example
 * const element = document.createElement('div');
 * isHidden(element);
 * // returns false
 *
 * @example
 * const element = document.createElement('div');
 * hideElement(element);
 * isHidden(element);
 * // returns true
 *
 */

export const isHidden = element => getCSSProperty(element, 'display') === 'none';
/**
 * Returns if an element is visible or not.
 *
 * @function isVisible
 *
 * @param {HTMLElement} element - the HTML element
 * @return {boolean} - if the element is visible or not
 *
 * @example
 * const element = document.createElement('div');
 * isVisible(element);
 * // returns true
 *
 * @example
 * const element = document.createElement('div');
 * hideElement(element);
 * isVisible(element);
 * // returns false
 *
 */

export const isVisible = element => !isHidden(element);
/**
 * Makes an element visible.
 *
 * @function displayElement
 *
 * @param {HTMLElement} element - the HTML element
 *
 * @example
 * const element = document.createElement('div');
 * displayElement(element);
 * // does nothing
 *
 * @example
 * const element = document.createElement('div');
 * hideElement(element);
 * isHidden(element);
 * // returns true
 * displayElement(element);
 * isVisible(element);
 * // returns true
 *
 */

export const displayElement = function (element) {
  let display = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'block';
  element.style.display = display;
};
/**
 * Makes an element hidden.
 *
 * @function hideElement
 *
 * @param {HTMLElement} element - the HTML element
 *
 * @example
 * const element = document.createElement('div');
 * hideElement(element);
 * isHidden(element);
 * // returns true
 *
 */

export const hideElement = element => {
  displayElement(element, 'none');
};
/**
 * Makes an element enabled.
 *
 * @function enableElement
 *
 * @param {HTMLElement} element - the HTML element
 *
 * @example
 * const element = document.createElement('input');
 * enableElement(element);
 * // does nothing
 *
 * @example
 * const element = document.createElement('input');
 * enableElement(element);
 * enableElement(element, { addClasses: ['enabled'], removeClasses: ['disabled'] })
 * elementIs('disabled', element);
 * // returns true
 * enableElement(element);
 * elementIs('disabled', element);
 * // returns false
 *
 */

export const enableElement = function (element) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    addClasses: [],
    removeClasses: []
  };
  element.disabled = false;
  addClass(element, options.addClasses);
  removeClass(element, options.removeClasses);
};
/**
 * Makes an element disabled.
 *
 * @function disableElement
 *
 * @param {HTMLElement} element - the HTML element
 *
 * @example
 * const element = document.createElement('input');
 * disableElement(element);
 * disableElement(element, { addClasses: ['disabled'], removeClasses: ['enabled'] });
 * elementIs('disabled', element);
 * // returns true
 *
 */

export const disableElement = function (element) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    addClasses: [],
    removeClasses: []
  };
  element.disabled = true;
  addClass(element, options.addClasses);
  removeClass(element, options.removeClasses);
};
/**
 * Makes an element visible if it is not visible.
 *
 * @function displayElementIfIsNotVisible
 *
 * @param {HTMLElement} element - the HTML element
 *
 * @example
 * const element = document.createElement('input');
 * displayElementIfIsNotVisible(element);
 * // does nothing
 *
 * @example
 * const element = document.createElement('input');
 * hideElement(element);
 * isHidden(element);
 * // returns true
 * displayElementIfIsNotVisible(element);
 * isVisible(element);
 * // returns true
 *
 */

export const displayElementIfIsNotVisible = function (element) {
  let display = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'block';
  if (isHidden(element)) displayElement(element, display);
};
/**
 * Makes an element hidden if it is visible.
 *
 * @function hideElementIfIsVisibe
 *
 * @param {HTMLElement} element - the HTML element
 *
 * @example
 * const element = document.createElement('input');
 * hideElementIfIsVisibe(element);
 * isHidden(element);
 * // returns true
 *
 * @example
 * const element = document.createElement('input');
 * hideElement(element);
 * isHidden(element);
 * // returns true
 * hideElementIfIsVisibe(element);
 * // does nothing
 *
 */

export const hideElementIfIsVisibe = element => {
  if (isVisible(element)) hideElement(element);
};
/**
 * Toggles element visibility
 *
 * @function toggleElement
 *
 * @param {HTMLElement} element - the HTML element
 * @param {String} display - the display to be applied to the element
 *
 * @example
 * const element = document.createElement('input');
 * isVisible(element);
 * // returns true
 * toggleElement(element);
 * isHidden(element);
 * // returns true
 *
 * @example
 * const element = document.createElement('input');
 * isVisible(element);
 * // returns true
 * hideElement(element);
 * isHidden(element);
 * // returns true
 * toggleElement(element);
 * isVisible(element);
 * // returns true
 *
 * @example
 * const element = document.createElement('input');
 * isVisible(element);
 * // returns true
 * hideElement(element);
 * isHidden(element);
 * // returns true
 * toggleElement(element, 'flex');
 * isVisible(element);
 * // returns true and applies 'flex' as the element's display
 *
 */

export const toggleElement = function (element) {
  let display = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'block';
  isHidden(element) ? displayElement(element, display) : hideElement(element);
};
/**
 * Toggles element enabling
 *
 * @function toggleEnableElement
 *
 * @param {(HTMLElement|HTMLElement[])} elements - an HTML element or a list of HTML elements
 *
 * @example
 * const element = document.createElement('input');
 * elementIs('disabled', element);
 * // returns false
 * toggleEnableElement(element);
 * elementIs('disabled', element);
 * // returns true
 *
 * @example
 * const email = document.createElement('input');
 * const password = document.createElement('input');
 * elementIs('disabled', email);
 * // returns false
 * elementIs('disabled', password);
 * // returns false
 * toggleEnableElement([email, password]);
 * elementIs('disabled', email);
 * // returns true
 * elementIs('disabled', password);
 * // returns true
 *
 * @example
 * const element = document.createElement('input');
 * disableElement(element);
 * elementIs('disabled', element);
 * // returns true
 * toggleEnableElement(element);
 * elementIs('disabled', element);
 * // returns false
 *
 */

export const toggleEnableElement = elements => {
  [elements].flat().forEach(element => element.disabled = !element.disabled);
};